import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { BellRing, CheckCircle2, Send, Smartphone, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminCard, AdminPageHeader, StatCard } from "@/components/admin/admin-shell";
import { azError } from "@/lib/az-errors";
import { categories } from "@/lib/data";
import { fetchActiveStores, storeSlug } from "@/lib/stores";
import {
  getPushStats,
  listPushHistory,
  sendAdminPush,
  sendInAppBroadcast,
  type PushAudience,
} from "@/lib/push.functions";

export const Route = createFileRoute("/_authenticated/admin/bildirisler")({
  component: Page,
});

const audienceLabels: Record<PushAudience, string> = {
  all: "Bütün cihazlar",
  users: "Yalnız qeydiyyatlı istifadəçilər",
  user: "Konkret istifadəçi",
};

function Page() {
  const qc = useQueryClient();
  const statsFn = useServerFn(getPushStats);
  const historyFn = useServerFn(listPushHistory);
  const sendFn = useServerFn(sendAdminPush);
  const inAppFn = useServerFn(sendInAppBroadcast);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("/");
  const [audience, setAudience] = useState<PushAudience>("all");
  const [targetUserId, setTargetUserId] = useState("");
  const [channel, setChannel] = useState<"both" | "inapp" | "push">("both");
  const [category, setCategory] = useState("");
  const [storeId, setStoreId] = useState("");

  const stores = useQuery({ queryKey: ["stores", "active"], queryFn: fetchActiveStores });

  const stats = useQuery({ queryKey: ["admin", "push", "stats"], queryFn: () => statsFn({}) });
  const history = useQuery({
    queryKey: ["admin", "push", "history"],
    queryFn: () => historyFn({}),
  });

  const send = useMutation({
    mutationFn: async () => {
      const payload = {
        data: { title, body, link, audience, targetUserId: targetUserId.trim() || null },
      };
      const inApp = channel !== "push" ? await inAppFn(payload) : null;
      const push = channel !== "inapp" ? await sendFn(payload) : null;
      return { inApp, push };
    },
    onSuccess: (r) => {
      const parts: string[] = [];
      if (r.inApp) parts.push(`${r.inApp.inserted} istifadəçiyə sayt daxili`);
      if (r.push) parts.push(`${r.push.success} cihaza push (${r.push.failed} uğursuz)`);
      toast.success(`Göndərildi: ${parts.join(", ")}`);
      setTitle("");
      setBody("");
      void qc.invalidateQueries({ queryKey: ["admin", "push"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  return (
    <div>
      <AdminPageHeader
        title="Bildiriş göndər"
        description="Bütün istifadəçilərə sayt daxili və brauzer push bildirişi göndər"
      />

      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        <StatCard
          label="Abunə cihazlar"
          value={String(stats.data?.devices ?? 0)}
          icon={Smartphone}
          hint="Bildirişə icazə vermiş aktiv cihazlar"
        />
        <StatCard
          label="Hesaba bağlı cihazlar"
          value={String(stats.data?.userDevices ?? 0)}
          icon={BellRing}
          hint="Qeydiyyatlı istifadəçilərin cihazları"
        />
      </div>

      <AdminCard className="mb-6 p-4 sm:p-6">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim() && body.trim()) send.mutate();
          }}
        >
          <div className="grid gap-2">
            <Label>Bildiriş növü</Label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: "both", label: "Hər ikisi" },
                  { key: "inapp", label: "Sayt daxili" },
                  { key: "push", label: "Brauzer push" },
                ] as const
              ).map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setChannel(o.key)}
                  aria-pressed={channel === o.key}
                  className={`rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                    channel === o.key
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-foreground"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Kimə göndərilsin</Label>
            <Select value={audience} onValueChange={(v) => setAudience(v as PushAudience)}>
              <SelectTrigger className="h-11 rounded-2xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(audienceLabels) as PushAudience[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {audienceLabels[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {audience === "user" && (
            <div className="grid gap-2">
              <Label>İstifadəçi ID</Label>
              <Input
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                placeholder="İstifadəçi ID"
                className="h-11 rounded-2xl"
              />
            </div>
          )}

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Kateqoriya (istəyə bağlı)</Label>
              <Select
                value={category || "none"}
                onValueChange={(v) => {
                  const slug = v === "none" ? "" : v;
                  setCategory(slug);
                  if (slug) {
                    setStoreId("");
                    setLink(`/kateqoriya/${slug}`);
                  }
                }}
              >
                <SelectTrigger className="h-11 rounded-2xl">
                  <SelectValue placeholder="Kateqoriya seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kateqoriya yoxdur</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Mağaza (istəyə bağlı)</Label>
              <Select
                value={storeId || "none"}
                onValueChange={(v) => {
                  const id = v === "none" ? "" : v;
                  setStoreId(id);
                  const st = (stores.data ?? []).find((s) => s.id === id);
                  if (st) {
                    setCategory("");
                    setLink(`/magaza/${storeSlug(st)}`);
                  }
                }}
              >
                <SelectTrigger className="h-11 rounded-2xl">
                  <SelectValue placeholder="Mağaza seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Mağaza yoxdur</SelectItem>
                  {(stores.data ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label>Başlıq</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Məsələn: Yeni kampaniya başladı"
              className="h-11 rounded-2xl"
            />
          </div>

          <div className="grid gap-2">
            <Label>Mətn</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Bildiriş mətni"
              className="min-h-24 rounded-2xl"
            />
          </div>

          <div className="grid gap-2">
            <Label>Keçid linki</Label>
            <Input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="/"
              className="h-11 rounded-2xl"
            />
          </div>

          <Button
            type="submit"
            disabled={send.isPending || !title.trim() || !body.trim()}
            className="h-11 rounded-2xl font-bold sm:w-48"
          >
            <Send className="mr-2 size-4" />
            {send.isPending ? "Göndərilir…" : "Göndər"}
          </Button>
        </form>
      </AdminCard>

      <h2 className="mb-3 text-lg font-bold">Göndərilmiş bildirişlər</h2>
      {history.isLoading && <p className="text-sm text-muted-foreground">Yüklənir…</p>}
      {!history.isLoading && (history.data?.length ?? 0) === 0 && (
        <AdminCard>
          <p className="p-10 text-center text-sm text-muted-foreground">
            Hələ bildiriş göndərilməyib.
          </p>
        </AdminCard>
      )}

      <div className="grid gap-3">
        {(history.data ?? []).map((n) => (
          <AdminCard key={n.id} className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold">{n.title}</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-xs text-muted-foreground">
                  {n.body}
                </p>
                {n.link && <p className="mt-1 text-[11px] text-primary">{n.link}</p>}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-[11px] text-muted-foreground">
                  {new Date(n.sent_at ?? n.created_at).toLocaleString("az-AZ")}
                </p>
                <div className="mt-1 flex items-center justify-end gap-3 text-xs font-bold">
                  <span className="flex items-center gap-1 text-success">
                    <CheckCircle2 className="size-3.5" />
                    {n.success_count ?? 0}
                  </span>
                  <span className="flex items-center gap-1 text-destructive">
                    <XCircle className="size-3.5" />
                    {n.fail_count ?? 0}
                  </span>
                </div>
              </div>
            </div>
          </AdminCard>
        ))}
      </div>
    </div>
  );
}
